import { isPlatform } from '@ionic/vue';
import { iosTransition } from './ios-transitions';
import { androidTransition } from './android-transitions';

/**
 * Platform Transition Delegate
 *
 * Detects the current platform and returns the appropriate transition:
 * - iOS native: uses iosTransition (400ms, full slide)
 * - Android native: uses androidTransition (300ms, Material Design with fade)
 * - Web: falls back to iOS transitions (consistent with app's iOS-first design)
 *
 * @param {HTMLElement} baseEl - Base element
 * @param {Object} opts - Animation options
 * @param {string} opts.direction - Navigation direction ('forward' | 'back')
 * @param {HTMLElement} opts.enteringEl - Entering page element
 * @param {HTMLElement} opts.leavingEl - Leaving page element
 * @returns {Function} Transition function for the detected platform
 */
export function platformTransition(baseEl, opts) {
  // Check if running on iOS native (not mobile web)
  const isIosNative = isPlatform('ios') && !isPlatform('mobileweb');

  // Check if running on Android native (not mobile web)
  const isAndroidNative = isPlatform('android') && !isPlatform('mobileweb');

  // Delegate to appropriate transition based on platform
  if (isIosNative) {
    return iosTransition(baseEl, opts);
  }

  if (isAndroidNative) {
    return androidTransition(baseEl, opts);
  }

  // Web fallback: use iOS transitions
  // This maintains consistency with the app's iOS-first design philosophy
  return iosTransition(baseEl, opts);
}

/**
 * Get the current platform type for animation purposes
 *
 * @returns {string} Platform type: 'ios' | 'android' | 'web'
 */
export function getAnimationPlatform() {
  if (isPlatform('ios') && !isPlatform('mobileweb')) {
    return 'ios';
  }
  if (isPlatform('android') && !isPlatform('mobileweb')) {
    return 'android';
  }
  return 'web';
}
